import classNames from "classnames";
import React from "react";

import { INPUT_SIZE_CLASSES } from "./Input.constants";
import { InputProps } from "./Input.types";

export const Input = React.forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  InputProps
>(
  (
    {
      as = "input",
      size = "md",
      error,
      fullWidth = true,
      leftIcon,
      rightIcon,
      inputClassName,
      iconClassName,
      className,
      ...props
    },
    ref,
  ) => {
    const controlClassName = classNames(
      "rounded-lg border bg-white font-medium text-[#1F2433] outline-none transition-all focus:outline-none focus:ring-0",
      "placeholder:text-[#9097A8]",
      "disabled:cursor-not-allowed disabled:bg-[#F8F9FB] disabled:text-[#9AA1AE]",
      INPUT_SIZE_CLASSES[size],
      fullWidth && "w-full",
      error
        ? "border-[#DC2626] focus:border-[#DC2626] focus-within:border-[#DC2626]"
        : "border-[#DCE2EA] hover:border-[#6B4EFF] focus:border-[#6B4EFF] focus:shadow-[0_0_0_1px_#6B4EFF] focus-within:border-[#6B4EFF] focus-within:shadow-[0_0_0_1px_#6B4EFF]",
      className,
    );
    const hasAdornment = !!leftIcon || !!rightIcon;

    const fieldClassName = hasAdornment
      ? classNames(
          "min-w-0 flex-1 border-0 bg-transparent p-0 font-Manrope text-current outline-none placeholder:text-[#9097A8] focus:ring-0",
          inputClassName,
        )
      : classNames(controlClassName, inputClassName);

    if (as === "textarea") {
      return (
        <textarea
          ref={ref as React.Ref<HTMLTextAreaElement>}
          className={fieldClassName}
          {...(props as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
        />
      );
    }

    if (hasAdornment) {
      return (
        <div
          className={classNames("flex items-center gap-2", controlClassName)}>
          {leftIcon && (
            <span
              className={classNames("shrink-0 text-[#9097A8]", iconClassName)}>
              {leftIcon}
            </span>
          )}
          <input
            ref={ref as React.Ref<HTMLInputElement>}
            className={fieldClassName}
            {...(props as React.InputHTMLAttributes<HTMLInputElement>)}
          />
          {rightIcon && (
            <span
              className={classNames(
                "ml-auto shrink-0 text-[#9097A8]",
                iconClassName,
              )}>
              {rightIcon}
            </span>
          )}
        </div>
      );
    }

    return (
      <input
        ref={ref as React.Ref<HTMLInputElement>}
        className={fieldClassName}
        {...(props as React.InputHTMLAttributes<HTMLInputElement>)}
      />
    );
  },
);

Input.displayName = "Input";
